/**
 * Step rail — progress trail of a reply and the ProcessFlow stages (design-v2).
 *
 * A 1px vertical line (border colour) through 6px dots, one per row, every
 * row a fixed height (26px default), growing only downwards. The line ends
 * square at the last dot — no decorative tip.
 *
 *   done     filled `marker` dot
 *   current  filled `marker` dot with a slow halo (none in reduced motion)
 *   stopped  grey dots (the whole rail, `status="stopped"`)
 *   pending  hollow dot
 *   static   neutral dots (ProcessFlow: stages, not progress)
 *
 * Height is always `steps.length × rowHeight` — changing `status` or a step's
 * `state` never shifts layout.
 *
 * A11y: an `<ol>` (name = `ariaLabel`), current row has `aria-current="step"`,
 * line/dots are `aria-hidden`. While `working`, a visually hidden
 * `aria-live="polite"` node announces only the current step text — pass
 * `live={false}` if the consumer owns it.
 */
import React from 'react';
import { Box } from '@mui/material';
import { useOctoTokens } from '../theme/octoTokens';
import { octoText, octoVisuallyHidden, REDUCED_MOTION } from '../theme/octoStyles';
import { haloPulse } from './keyframes';

/** Left gutter that holds the rail; message text columns align to it. */
export const STEP_GUTTER = 22;
export const STEP_ROW = 26;
/** Centre of the line / dots inside the gutter. */
const LINE_X = 10;
const DOT = 6;

export type StepRailStatus = 'working' | 'done' | 'stopped' | 'static';
export type StepState = 'done' | 'current' | 'pending';

export interface StepRailStep {
  id: string;
  /** Row content. A string gets the step typography + single-line ellipsis. */
  label: React.ReactNode;
  state: StepState;
  /** Text announced when this step becomes current (defaults to `label` if string). */
  announce?: string;
}

export interface StepRailProps {
  steps: StepRailStep[];
  status?: StepRailStatus;
  /** Fixed row height; ≥26. */
  rowHeight?: number;
  ariaLabel?: string;
  /** Own the aria-live announcement of the current step (default true). */
  live?: boolean;
  className?: string;
  'data-testid'?: string;
}

const StepRail: React.FC<StepRailProps> = ({
  steps,
  status = 'working',
  rowHeight = STEP_ROW,
  ariaLabel = 'Etapas',
  live = true,
  className,
  'data-testid': testId,
}) => {
  const t = useOctoTokens();
  const c = t.color;
  if (steps.length === 0) return null;

  const stopped = status === 'stopped';
  const n = steps.length;
  const half = rowHeight / 2;
  const current = steps.find((s) => s.state === 'current');
  const liveText =
    status === 'working' && current
      ? current.announce ?? (typeof current.label === 'string' ? current.label : '')
      : '';

  const dotSx = (state: StepState) => {
    const isCurrent = state === 'current' && status === 'working';
    const grey = stopped || status === 'static';
    const color = grey ? c.text3 : c.marker;
    const hollow = state === 'pending';
    return {
      position: 'absolute',
      left: LINE_X - DOT / 2,
      top: half - DOT / 2,
      width: DOT,
      height: DOT,
      boxSizing: 'border-box',
      borderRadius: '50%',
      // Opaque centre so the line never shows through a hollow dot.
      bgcolor: hollow ? 'var(--oc-msg-bg, transparent)' : color,
      border: hollow ? `1px solid ${c.text3}` : 'none',
      transition: `background-color ${t.motion.state}ms ${t.motion.easeInk}`,
      ...(isCurrent
        ? {
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              bgcolor: color,
              animation: `${haloPulse} 2000ms ${t.motion.easeInk} infinite`,
              [REDUCED_MOTION]: { animation: 'none', opacity: 0 },
            },
          }
        : null),
    } as const;
  };

  return (
    <Box
      className={className}
      data-testid={testId}
      data-octo="step-rail"
      data-status={status}
      sx={{ position: 'relative', height: n * rowHeight }}
    >
      {n > 1 ? (
        <Box
          aria-hidden
          data-octo="step-rail-line"
          sx={{
            position: 'absolute',
            left: LINE_X - 0.5,
            top: half,
            width: '1px',
            height: (n - 1) * rowHeight,
            bgcolor: c.hairlineStrong,
          }}
        />
      ) : null}

      <Box
        component="ol"
        role="list"
        aria-label={ariaLabel}
        sx={{ listStyle: 'none', m: 0, p: 0, position: 'relative' }}
      >
        {steps.map((step) => {
          const isCurrent = step.state === 'current';
          return (
            <Box
              component="li"
              key={step.id}
              aria-current={isCurrent ? 'step' : undefined}
              data-state={step.state}
              sx={{
                position: 'relative',
                height: rowHeight,
                display: 'flex',
                alignItems: 'center',
                pl: `${STEP_GUTTER}px`,
                minWidth: 0,
              }}
            >
              <Box component="span" aria-hidden data-octo="step-dot" sx={dotSx(step.state)} />
              {typeof step.label === 'string' ? (
                <Box
                  component="span"
                  sx={{
                    ...octoText(t, isCurrent && !stopped ? 'stepCurrent' : 'step'),
                    ...(step.state === 'pending' ? { color: c.text3 } : null),
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {step.label}
                </Box>
              ) : (
                step.label
              )}
            </Box>
          );
        })}
      </Box>

      {live ? (
        <Box component="span" aria-live="polite" sx={octoVisuallyHidden}>
          {liveText}
        </Box>
      ) : null}
    </Box>
  );
};

export default StepRail;
